import { DatePipe } from '@angular/common';
import { Component, AfterViewInit, ViewChild, ElementRef, ViewEncapsulation } from '@angular/core';
import { ShareddtaService } from '@app/services/shareddta.service';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { EditadminComponent } from '@app/components/editadmin/editadmin.component';
import { CountryPhoneCodes } from '@app/components/interface/country-phone-codes';
import { AuthServiceService } from '@app/services/auth-service.service';
import { DialogService } from '@app/services/dialog.service';
import { ToastService } from '@app/services/toast.service';
import { Subscription, Observable, skip } from 'rxjs';
import { Router } from '@angular/router';
import { BreadcrumbService } from '@app/services/breadcrumb.service';
interface Admin {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  countryCode?: string;
  department: string;
  role: string;
  joinedDate: string;
  avatar?: string;
  expanded?: boolean;
}

interface SubscriptionPlan {
  name: string;
  expiryDate: string;
  price: number;
  maxAdmins: number;
  maxDrivers: number;
  maxLocations: number;
  archiveRetention: string;
  status: string;
}

interface UserProfile {
  firstName: string;
  lastName: string;
  email: string;
  countryCode: string;
  phone: string;
  emailVerified: boolean;
  avatar?: string;
}
@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class ProfileComponent implements AfterViewInit {
  countryCodes: any = CountryPhoneCodes;
  // Tab Management
  activeTab: 'profile' | 'admins' = 'profile';
  isLoading: boolean = true;
  isUpdatingProfile: boolean = false;
  isUpdatingPassword: boolean = false;
  isSubmittingAdmin: boolean = false;
  isUploadingPhoto: boolean = false;
  private profileSub!: Subscription;
  private adminSub!: Subscription;

  // Profile Data
  userProfile: UserProfile = {
    firstName: 'John',
    lastName: 'Doe',
    email: 'john.doe@company.com',
    countryCode: '+1',
    phone: '1234567890',
    emailVerified: true
  };

  // Subscription Data
  subscription: any;
  organization: any;
  showSecretKey = false;

  // Admin List
  admins: any;

  totalAdmins = 0;



  // Forms
  profileForm!: FormGroup;
  passwordForm!: FormGroup;
  addAdminForm!: FormGroup;

  // Modal
  showAddAdminModal = false;
  adminPhotoPreview: string | null = null;
  @ViewChild('adminPhotoInputRef') adminPhotoInputRef!: ElementRef<HTMLInputElement>;

  triggerAdminPhotoInput(): void {
    if (this.adminPhotoInputRef?.nativeElement) {
      this.adminPhotoInputRef.nativeElement.click();
    }
  }

  // Password Visibility
  showNewPassword = false;
  showConfirmPassword = false;

  constructor(
    private fb: FormBuilder, private authService: AuthServiceService, private toast: ToastService, private dialogService: DialogService, private dialog: MatDialog, private sharedData: ShareddtaService, private router: Router, private breadcrumbService: BreadcrumbService
  ) { }

  ngAfterViewInit(): void {
    this.sharedData.signalPageReady(); // dismiss main spinner immediately, shimmer handles loading state
  }

  ngOnInit(): void {
    this.sharedData.signalPageReady(); // also signal here in case AfterViewInit is too late
    this.breadcrumbService.set([
      { label: 'Dashboard', icon: 'M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25', action: () => this.router.navigate(['/admin/dashboard']) },
      { label: 'My Profile' }
    ]);
    this.initializeForms();
    this.loadProfileData();

    const savedCountryCode = localStorage.getItem('countryCode');
    this.profileForm.patchValue({
      countryCode: savedCountryCode
    })
  }

  initializeForms(): void {
    // Profile Form
    this.profileForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      countryCode: ['', Validators.required],
      phone: ['', [Validators.required, Validators.pattern('^[0-9]{10}$')]],
      email: [{ value: '', }]
    });

    // Password Form
    this.passwordForm = this.fb.group({
      newPassword: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', Validators.required]
    }, { validators: this.passwordMatchValidator });

    // Add Admin Form
    this.addAdminForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      countryCode: ['', Validators.required],
      phone: ['', [Validators.pattern('(^NA$)|(^[0-9]{10}$)'), // NA or exactly 10 digits
      ]],
      department: ['shipping', Validators.required]
    });
  }

  passwordMatchValidator(group: FormGroup): { [key: string]: boolean } | null {
    const newPassword = group.get('newPassword')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    return newPassword === confirmPassword ? null : { passwordMismatch: true };
  }

  loadProfileData(): void {
    this.getProfile();
    this.getAdminList();
  }

  // Tab Switching
  switchTab(tab: 'profile' | 'admins'): void {
    this.activeTab = tab;
  }

  // Profile Actions
  profileUpdate$: any;
  saveProfile(): void {
    if (this.profileForm.invalid || this.isUpdatingProfile) {
      if (this.profileForm.invalid) {
        this.profileForm.markAllAsTouched();
        this.toast.show('error', 'Please fill all required fields correctly');
      }
      return;
    }
    this.isUpdatingProfile = true;
    const formData = new FormData();
    formData.append('first_name', this.profileForm.get('firstName')?.value);
    formData.append('last_name', this.profileForm.get('lastName')?.value);
    formData.append('phone_number', this.profileForm.get('phone')?.value);
    formData.append('email', this.profileForm.get('email')?.value);
    formData.append('country_code', this.profileForm.get('countryCode')?.value);
    this.profileUpdate$ = this.authService.profileUpdate(this.adminId, formData);

    this.profileUpdate$.subscribe(
      (data: any) => {
        this.isUpdatingProfile = false;
        if (data.resultDescription) {
          this.toast.show('success', data.resultDescription);
          this.getProfile();
        }
        if (data.errorMessage) {
          this.toast.show('error', data.errorMessage);
        }
      },
      (err: any) => {
        this.isUpdatingProfile = false;
        this.toast.show('error', 'Failed to Update');
      }
    );
  }




  cancelProfileEdit(): void {
    this.profileForm.patchValue(this.userProfile);
  }

  changePassword(): void {
    // if (this.passwordForm.valid) {
    //   const { newPassword } = this.passwordForm.value;
    //   this.profileService.changePassword(newPassword).subscribe(
    //     (response) => {
    //       console.log('Password changed:', response);
    //       alert('Password changed successfully!');
    //       this.passwordForm.reset();
    //     },
    //     (error) => {
    //       console.error('Error changing password:', error);
    //       alert('Failed to change password');
    //     }
    //   );
    // }
  }

  togglePasswordVisibility(field: 'new' | 'confirm'): void {
    if (field === 'new') {
      this.showNewPassword = !this.showNewPassword;
    } else {
      this.showConfirmPassword = !this.showConfirmPassword;
    }
  }
  selectedProfilePhoto: File | null = null;
  onProfilePhotoChange(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input.files && input.files[0]) {
      const file = input.files[0];
      this.selectedProfilePhoto = file;
      this.isUploadingPhoto = true;
      const reader = new FileReader();
      reader.onload = () => {
        this.profileData.profile_image = reader.result as string;
      };

      reader.readAsDataURL(file);
      const formData = new FormData();
      formData.append('profile_image', this.selectedProfilePhoto);
      this.profileUpdate$ = this.authService.profileUpdate(this.adminId, formData);

      this.profileUpdate$.subscribe({
        next: (data: any) => {
          this.isUploadingPhoto = false;
          if (data.resultDescription) {
            this.toast.show('success', data.resultDescription);
            this.getProfile();
          }
          if (data.errorMessage) {
            this.toast.show('error', data.errorMessage);
          }
        },
        error: (err: any) => {
          this.isUploadingPhoto = false;
          this.toast.show('error', 'Failed to Update');
        }
      });
    }
  }



  toggleAdminDetails(index: number): void {
    this.admins[index].expanded = !this.admins[index].expanded;
  }
  isEditAdmin = false;
  optionType: any;
  editingAdminId: number | null = null;

  editAdmin(admin: any): void {

    this.isEditAdmin = true;
    this.editingAdminId = admin.id; // backend id
    this.adminPhotoPreview = admin.profile_image;
    this.addAdminForm.patchValue({
      firstName: admin.first_name,
      lastName: admin.last_name,
      email: admin.email,
      countryCode: admin.country_code || '+91',
      phone: (admin.phone_number && admin.phone_number !== 'Not Available')
        ? admin.phone_number
        : 'NA'
      ,
      department: admin.department
    });
    const isShip = admin.is_shipped_admin;
    const isReceive = admin.is_receiving_admin;

    if (isShip && isReceive) {
      this.optionType = "both";
    } else if (isShip) {
      this.optionType = "shipping";
    } else if (isReceive) {
      this.optionType = "receiving";
    } else {
      this.optionType = "";
    }

    this.showAddAdminModal = true;
    document.body.style.overflow = 'hidden';;
  }

  // deleteAdmin(index: number): void {
  //   const admin = this.admins[index];
  //   if (confirm(`Are you sure you want to delete ${admin.firstName} ${admin.lastName}?`)) {
  //     // this.profileService.deleteAdmin(admin.id).subscribe(
  //     //   (response) => {
  //     //     console.log('Admin deleted:', response);
  //     //     this.admins.splice(index, 1);
  //     //     this.totalAdmins--;
  //     //     alert('Admin deleted successfully!');
  //     //   },
  //     //   (error) => {
  //     //     console.error('Error deleting admin:', error);
  //     //     alert('Failed to delete admin');
  //     //   }
  //     // );
  //   }
  // }

  callAdmin(admin: any): void {
    if (admin.phone_number) {
      window.location.href = `tel:${admin.phone_number}`;
    }
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.toast.show('success', ' Copied!');;
    });
  }
  copyToClipboardPhone(countryCode: string, phone: string): void {

    // Handle NA case
    if (!phone || phone === 'NA') {
      this.toast.show('error', 'No phone number to copy!');
      return;
    }

    const fullNumber = `${countryCode}${phone}`;

    navigator.clipboard.writeText(fullNumber).then(() => {
      this.toast.show('success', 'Copied!');
    });
  }

  toggleSecretKey(): void {
    this.showSecretKey = !this.showSecretKey;
  }

  copySecretKey(): void {
    if (this.organization?.api_key) {
      navigator.clipboard.writeText(this.organization.api_key).then(() => {
        this.toast.show('success', 'Secret Key Copied!');
      });
    }
  }


  getAdminInitials(admin: any): string {
    return `${admin.first_name.charAt(0)}${admin.last_name.charAt(0)}`.toUpperCase();
  }



  // Add Admin Modal
  openAddAdminModal(): void {
    this.showAddAdminModal = true;
    document.body.style.overflow = 'hidden';
    this.isEditAdmin = false;
    const savedCountryCode = localStorage.getItem('countryCode');
    this.addAdminForm.patchValue({
      countryCode: savedCountryCode,
      department: 'shipping'
    });
  }

  closeAddAdminModal(): void {
    this.showAddAdminModal = false;
    document.body.style.overflow = '';
    this.addAdminForm.reset();
    this.adminPhotoPreview = null;
  }
  selectedAdminPhoto: File | null = null;
  onAdminPhotoChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      this.selectedAdminPhoto = file;
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        if (e.target?.result) {
          this.adminPhotoPreview = e.target.result as string;
        }
      };
      reader.readAsDataURL(file);
    }
  }

  addAdmin(): void {
    if (this.addAdminForm.invalid || this.isSubmittingAdmin) {
      if (this.addAdminForm.invalid) {
        this.addAdminForm.markAllAsTouched();
      }
      return;
    }
    this.isSubmittingAdmin = true;

    let isShipped = "False";
    let isReceiving = "False";

    if (this.optionType === "both") {
      isShipped = "True";
      isReceiving = "True";
    }
    else if (this.optionType === "shipping") {
      isShipped = "True";
      isReceiving = "False";
    }
    else if (this.optionType === "receiving") {
      isShipped = "False";
      isReceiving = "True";
    }

    // 👉 FORM DATA CREATE
    const formData = new FormData();

    formData.append('first_name', this.addAdminForm.value.firstName);
    formData.append('last_name', this.addAdminForm.value.lastName);
    formData.append('country_code', this.addAdminForm.value.countryCode);

    if (this.addAdminForm.value.phone !== 'NA') {
      formData.append('phone_number', this.addAdminForm.value.phone);
    }

    formData.append('is_shipped_admin', isShipped);
    formData.append('is_receiving_admin', isReceiving);
    formData.append('email', this.addAdminForm.value.email);

    // 👉 IMAGE FILE ONLY IF SELECTED
    if (this.selectedAdminPhoto) {
      formData.append('profile_image', this.selectedAdminPhoto);
    }

    // API CALL WITH FORMDATA
    this.authService.addSuperAdmin(formData).subscribe(
      (data: any) => {
        this.isSubmittingAdmin = false;
        if (this.authService.resultCodeError(data)) {
          return;
        }
        this.getAdminList();
        this.toast.show('success', data.resultDescription);
        this.closeAddAdminModal();
      },
      (error) => {
        this.isSubmittingAdmin = false;
        this.authService.dataError(error);
      }
    );
  }


  updateAdmin(): void {
    if (this.addAdminForm.invalid || this.isSubmittingAdmin) {
      if (this.addAdminForm.invalid) {
        this.addAdminForm.markAllAsTouched();
      }
      return;
    }
    this.isSubmittingAdmin = true;

    let isShipped = "False";
    let isReceiving = "False";

    if (this.optionType === "both") {
      isShipped = "True";
      isReceiving = "True";
    }
    else if (this.optionType === "shipping") {
      isShipped = "True";
      isReceiving = "False";
    }
    else if (this.optionType === "receiving") {
      isShipped = "False";
      isReceiving = "True";
    }

    // 👉 CREATE FORMDATA FOR UPDATE
    const formData = new FormData();

    formData.append('first_name', this.addAdminForm.value.firstName);
    formData.append('last_name', this.addAdminForm.value.lastName);
    formData.append('country_code', this.addAdminForm.value.countryCode);

    if (this.addAdminForm.value.phone !== 'NA') {
      formData.append('phone_number', this.addAdminForm.value.phone);
    }

    formData.append('is_shipped_admin', isShipped);
    formData.append('is_receiving_admin', isReceiving);

    // 👉 IMPORTANT: Only append image if new image selected
    if (this.selectedAdminPhoto) {
      formData.append('profile_image', this.selectedAdminPhoto);
    }

    // CALL UPDATE API WITH FORMDATA
    this.authService.editAdmin(Number(this.editingAdminId), formData).subscribe(
      (data: any) => {
        this.isSubmittingAdmin = false;
        if (this.authService.resultCodeError(data)) {
          return;
        }

        this.getAdminList();
        this.toast.show('success', data.resultDescription);
        this.closeAddAdminModal();
      },
      (error) => {
        this.isSubmittingAdmin = false;
        this.authService.dataError(error);
      }
    );
  }


  // Subscription Actions
  companyid:any;
  upgradePlan(): void {
    // console.log('Upgrade plan clicked');
    // alert('Upgrade plan functionality');
      this.companyid = localStorage.getItem('org_Id')
  this.router.navigate(['admin/update-plans', this.companyid]);
  }

  cancelSubscription(): void {
    // if (confirm('Are you sure you want to cancel your subscription?')) {
    //   console.log('Cancel subscription clicked');
    //   alert('Cancel subscription functionality');
    // }
    this.showLogoutPopup = true;
  }
  onKeyPress(event: KeyboardEvent) {
    const input = event.target as HTMLInputElement;
    const key = event.key;

    // Allow only digits (0-9)
    if (!/^\d$/.test(key)) {
      event.preventDefault(); // Block non-digit input
      return;
    }

    // Block input if already 10 digits
    if (input.value.length >= 10) {
      event.preventDefault();
    }
  }
  admincount: any;
  adminList$!: Observable<object[]>;
  getAdminList() {
    this.adminSub = this.authService.getAdmin().pipe(skip(1)).subscribe(
      (data: any) => {
        if (!data) return;
        console.log('API Response:', data);
        // Handle both top-level and result-wrapped responses
        this.admins = data?.results || data?.result?.results || [];
        this.totalAdmins = data?.count || data?.result?.count || 0;
        this.checkLoadingState();
      },
      (error) => {
        console.error('API Error:', error);
        this.checkLoadingState();
      }
    );
  }
  async deleteAdmin(adminId: any): Promise<void> {
    const confirmed = await this.dialogService.confirm(
      'Delete Admin',
      'Are you sure you want to delete this admin? This action cannot be undone.',
      'Delete',
      'Cancel'
    );

    if (confirmed?.confirmed) {
      // console.log("FormData Value:", formdata.get('order_ids'));
      this.authService.deleteAdmin(adminId).subscribe((data: any) => {
        if (this.authService.resultCodeError(data)) {
          return;
        }
        this.toast.show('success', 'Admin has been deleted successfully');
        this.getAdminList();
      }, () => {
        this.toast.show('error', "Unable to Delete");
      })
    }
  }
  async toggleAdminStatus(admin: any): Promise<void> {
    const isActive = admin.admin_status !== false && admin.is_active !== false;
    const action = isActive ? 'Deactivate' : 'Activate';
    const confirmed = await this.dialogService.confirm(
      `${action} Admin`,
      `Are you sure you want to ${action.toLowerCase()} this admin?`,
      action,
      'Cancel'
    );

    if (confirmed?.confirmed) {
      this.authService.activateDeactivateAdmin(admin.id, !isActive).subscribe((data: any) => {
        if (this.authService.resultCodeError(data)) {
          return;
        }
        this.toast.show('success', `Admin has been ${action.toLowerCase()}d successfully`);
        this.getAdminList();
      }, () => {
        this.toast.show('error', `Unable to ${action.toLowerCase()} Admin`);
      });
    }
  }
  convertToLocalDate(utcDateString: string | null): string {
    if (!utcDateString) {
      return ''; // Or any default value you prefer
    }


    const datePipe = new DatePipe('en-US');
    const utcDate = new Date(utcDateString);

    const formattedDate = datePipe.transform(utcDate, 'EEE MMM dd yyyy') || '';


    return formattedDate;
  }
  profile$!: Observable<object[]>;
  profileData: any;
  adminId: any;
  phone_number: any;
  country_codes: any
  showVerifyButton: boolean = false;
  ownerPhoneVerified: boolean = true;
  verifyphoneotp: boolean = false;
  getProfile() {
    this.profileSub = this.authService.getProfile().pipe(skip(1)).subscribe((data: any) => {
      this.profileData = data?.result.admin;
      this.organization = data?.result.organization;
      if (this.organization) {
        this.organization.api_key = data?.result.organization.api_key || data?.result.api_key || data?.result.organization.company?.api_key || data?.result.admin?.api_key;
      }
      this.subscription = data?.result.organization.current_plan;
      this.profileForm.patchValue({
        firstName: data?.result.admin.first_name,
        lastName: data?.result.admin.last_name,
        countryCode: data?.result.admin.country_code,
        phone: data?.result.admin.phone_number || 'NA',
        email: data?.result.admin.email
      });
      this.profileForm.markAsPristine();
      this.adminId = data?.result.admin.id;
      this.phone_number = data?.result.admin.phone_number;
      this.country_codes = data?.result.admin.country_code;
      this.profileForm.get('phone')?.valueChanges.subscribe(val => {
        this.showVerifyButton = val !== this.profileData?.phone_number && val?.length === 10;
        if (this.showVerifyButton == true) {
          this.ownerPhoneVerified = false;
        }
        if (this.showVerifyButton == false) {
          this.ownerPhoneVerified = true;
          this.verifyphoneotp = false;
        }
      });
      this.checkLoadingState();
      // this.userProfile = data?.result
    }, (error) => {
      this.checkLoadingState();
    })
  }

  private profileLoaded = false;
  private adminsLoaded = false;

  private checkLoadingState() {
    if (this.profileData && this.admins) {
      this.isLoading = false;
      this.sharedData.signalPageReady();
    }
  }

  ngOnDestroy(): void {
    if (this.profileSub) this.profileSub.unsubscribe();
    if (this.adminSub) this.adminSub.unsubscribe();
  }
  loading: boolean = false;
  sendOTP(admin?: any, showChangePassword?: any) {
    if (this.passwordForm.controls['newPassword'].status == 'INVALID' ||
      this.passwordForm.controls['confirmPassword'].status == 'INVALID' ||
      !(this.passwordForm.value.newPassword?.length >= 6) ||
      (this.passwordForm.value.confirmPassword != this.passwordForm.value.newPassword)) {
      this.passwordForm.markAllAsTouched();
      return;
    }
    showChangePassword = true;
    const phoneControl = this.profileForm.get('phone');

    if (phoneControl?.value == 'NA') {
      this.emailotphint = true;
      this.inputType = 'email'
      this.sentOTPEmail();
    }
    else {
      this.openModal(admin, showChangePassword)
    }
  }
  emailsendotp: any;
  isOtpSent: boolean = false;
  sentOTPEmail() {
    const submittedInput = this.authService.getLS('org_email') || '';
    this.emailsendotp = submittedInput
    this.authService.setEmail(submittedInput); // Use email instead of phone

    // Send OTP using email logic
    this.authService.sendResetOtp(submittedInput).subscribe( // Send email without country code
      (data: any) => {
        setTimeout(() => {
        }, 300);
        if (this.authService.resultCodeError(data)) {
          return;
        }

        this.toast.show('success', 'OTP Sent to your email!');
        this.isOtpSent = true;
        this.isResendOtpDisabled = true;
        this.startResendOtpTimer();
      },
      (error) => {
        this.authService.dataError(error);
      }
    );
  }
  inputType: 'email' | 'phone' | null = null;
  emailotphint: any;
  openModal(admin: any, showChangePassword?: any) {
    let enterAnimationDuration = '200ms';
    let exitAnimationDuration = '200ms';
    const profileData = this.profileData
    const dialogRef = this.dialog.open(EditadminComponent, {
      width: '700px',
      enterAnimationDuration,
      exitAnimationDuration,
      data: {
        admin,   // the admin object
        showChangePassword,
        profileData
        // additional property to control behavior
      },// Pass the admin data to the modal
    });

    dialogRef.afterClosed().subscribe((updatedAdmin: any) => {
      if (showChangePassword == false) {
        if (updatedAdmin) {
          // updatedAdmin now has full object with all required fields
          console.log(updatedAdmin);
          // Call the API with full payload
          this.authService.editAdmin(admin.id, updatedAdmin).subscribe(
            (data: any) => {
              if (this.authService.resultCodeError(data)) {
                return;
              }
              this.getAdminList();
              this.toast.show('success', data?.resultDescription);
            },
            (error) => {
              this.authService.dataError(error);
            }
          );
        }
        else {
          this.getAdminList();
        }
      }
      if (showChangePassword == true) {
        if (updatedAdmin === 'phone') {
          this.inputType = 'phone'
          console.log('User clicked Phone Number');
          this.sendOTP1();
          this.emailotphint = false;
          // Handle accordingly
        } else if (updatedAdmin === 'email') {
          this.inputType = 'email'
          console.log('User clicked Email');
          this.emailotphint = true;
          this.sentOTPEmail();
          // Handle accordingly
        } else {
          console.log('Modal closed without selection');
        }
      }

    });

  }
  sendOtp$!: Observable<any>;
  sendOTP1() {
    if (this.passwordForm.controls['newPassword'].status == 'INVALID' ||
      this.passwordForm.controls['confirmPassword'].status == 'INVALID' ||
      !(this.passwordForm.value.newPassword?.length >= 6) ||
      (this.passwordForm.value.confirmPassword != this.passwordForm.value.newPassword)) {
      this.passwordForm.markAllAsTouched();
      return;
    }

    this.sendOtp$ = this.authService.sendMobileOtp(this.phone_number, this.country_codes);
    this.authService.setMobileNumber(this.phone_number);
    this.authService.setCountryCode(this.country_codes);
    this.sendOtp$.subscribe((data: any) => {
      this.isOtpSent = true;
      this.isResendOtpDisabled = true;
      this.startResendOtpTimer();
      if (data.resultCode == 0) {
        this.isOtpSent = false;
        this.isResendOtpDisabled = false;
        this.toast.show('error', data.message);
      }
      if (data.resultCode != 0) {
        this.isOtpSent = true;
        this.startResendOtpTimer();
        this.isResendOtpDisabled = true;
        this.toast.show('success', data.message);
      }
    }, (err) => {
      this.toast.show('error', "Failed to Send an OTP");
    });
  }
  onOtpPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text');

    if (pastedData && /^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split('');
      this.otp = digits;
      // Update each input box value and focus last
      setTimeout(() => {
        for (let i = 0; i < digits.length; i++) {
          const input = document.getElementById(`otp${i + 1}`) as HTMLInputElement;
          if (input) {
            input.value = digits[i];
          }
        }
        // Focus last filled input
        const lastInput = document.getElementById(`otp${digits.length}`) as HTMLInputElement;
        if (lastInput) {
          lastInput.focus();
        }
      }, 0);
    }
  }

  verifyOtp(): void {
    // this.otpTouched = true;
    const otpValue = this.otp.join('');

    if (otpValue.length !== 6) {
      this.otpTouched = true;
      return;
    }

    const payload = {
      email: this.profileForm.get('phone')?.value,          // ✔ phone goes in email field (backend format)
      country_code: this.profileForm.value.countryCode, // ✔ only send in phone OTP case
      user_otp: otpValue     // ✔ OTP
    };

    this.authService.verifyOtp(payload).subscribe(
      (res: any) => {
        console.log("Phone OTP Verified", res);

        if (res.resultCode == 1) {
          this.verifyphoneotp = false;
          this.ownerPhoneVerified = true;
          this.showVerifyButton = false;
          this.toast.show('success', res.message);
        }
        else if (res.resultCode == 0) {
          this.toast.show('error', res.errorMessage);
        }
      },
      (error) => {
        this.toast.show('error', "Something went wrong");
      }
    );
  }

  onResendOtp(): void {
    if (this.isResendOtpDisabled) {
      return;
    }
    if (this.emailotphint === true) {
      this.sentOTPEmail();
    }
    else {
      this.sendOTP1();
    }
  }
  otp = ['', '', '', '', '', ''];
  clearOtp(): void {
    this.otp = ['', '', '', '', '', ''];
    for (let i = 1; i <= 6; i++) {
      const input = document.getElementById(`otp${i}`) as HTMLInputElement;
      if (input) {
        input.value = '';
      }
    }
  }
  resendOtpTimer = 0;
  isResendOtpDisabled = false;
  getResendOtpTimerDisplay(): string {
    return this.resendOtpTimer.toString().padStart(2, '0');
  }
  otpTouched = false;
  get isOtpInvalid(): boolean {
    return this.otpTouched && this.otp.some(d => d === '');
  }
  private _otpProcessing = false;
  onOtpInput(event: any, index: number): void {
    if (this._otpProcessing) return; // ignore if we're already handling an event
    const input = event.target as HTMLInputElement;
    let value = input.value;

    // Normalize: keep only the last character if multiple entered
    // ignore programmatic input events
    if ((input.dataset as any).programmatic) {
      delete (input.dataset as any).programmatic;
      return;
    }

    if (!value) {
      input.value = '';
      this.otp[index] = '';
      return;
    }
    if (value.length > 1) {
      value = value.slice(-1);
      input.value = value;
    }

    // Allow only single digit
    if (!/^[0-9]$/.test(value)) {
      input.value = '';
      this.otp[index] = '';
      return;
    }

    // mark as processing to prevent duplicate handling
    this._otpProcessing = true;
    this.otp[index] = value;

    // Move to next input (deferred to avoid double-fill due to key events)
    if (index < this.otp.length - 1) {
      setTimeout(() => document.getElementById(`otp${index + 1}`)?.focus(), 0);
    }

    // Auto verify when all digits filled
    if (this.otp.every(digit => digit !== '')) {
      // this.verifyOtp();
    }

    // release processing flag shortly after
    setTimeout(() => { this._otpProcessing = false; }, 50);
  }
  onOtpKeydown(event: KeyboardEvent, index: number): void {
    const key = event.key;

    // Allow Tab navigation
    if (key === 'Tab') {
      return;
    }

    // Handle Backspace
    if (key === 'Backspace') {
      event.preventDefault();
      this.otp[index] = '';

      if (index > 0) {
        document.getElementById(`otp${index}`)?.focus();
      }
      return;
    }

    // Allow only digits
    if (/^[0-9]$/.test(key)) {
      event.preventDefault();

      // ✅ ONLY update model
      this.otp[index] = key;

      // ✅ move focus
      if (index < this.otp.length - 1) {
        setTimeout(() => {
          document.getElementById(`otp${index + 2}`)?.focus();
        }, 0);
      }

      // Auto verify
      if (this.otp.every(d => d !== '')) {
        // this.verifyOtp();
      }
    }
  }
  resendOtpTimerInterval: any = null;
  startResendOtpTimer(): void {
    this.resendOtpTimer = 60;
    this.isResendOtpDisabled = true;

    if (this.resendOtpTimerInterval) {
      clearInterval(this.resendOtpTimerInterval);
    }

    this.resendOtpTimerInterval = setInterval(() => {
      this.resendOtpTimer--;

      if (this.resendOtpTimer <= 0) {
        clearInterval(this.resendOtpTimerInterval);
        this.isResendOtpDisabled = false;
        this.resendOtpTimer = 0;
      }
    }, 1000);
  }
  verifyOtp$!: Observable<any>;
  updatePassword() {
    const otpValue = this.otp.join('');
    if (otpValue.length !== 6 || this.isUpdatingPassword) {
      if (otpValue.length !== 6) {
        this.otpTouched = true;
      }
      this.toast.show('error', "Please enter a valid 6-digit OTP");
      return;
    }
    this.isUpdatingPassword = true;

    const phoneControl = this.profileForm.get('phone');
    if (phoneControl?.value == 'NA') {
      const submittedInput = this.authService.getLS('org_email') || ''
      this.verifyOtp$ = this.authService.verifyResetOtp(submittedInput, otpValue);
    }
    else {
      this.verifyOtp$ = this.authService.verifyResetOtp(this.phone_number, otpValue,this.country_codes);
    }
    // this.verifyOtp$ = this.authService.verifyResetOtp(this.phone_number, this.otp);

    this.verifyOtp$.subscribe((data: any) => {
      if (data.resultCode == '1') {
        this.toast.show('success', "Please Wait...");
        this.resetPassword();
      } else {
        this.isUpdatingPassword = false;
        this.toast.show('error', data.errorMessage);
      }
    }, (err) => {
      this.isUpdatingPassword = false;
      this.toast.show('error', "Failed to Verify an OTP");
    });
  }
  resetPassword$!: any;
  resetPassword() {
    this.resetPassword$ = this.authService.resetPassword(this.profileData?.email, this.passwordForm.value.newPassword);
    this.resetPassword$.subscribe((data: any) => {
      this.isUpdatingPassword = false;
      this.isOtpSent = false;
      this.passwordForm.patchValue({
        newPassword: '',
        confirmPassword: ''
      });
      this.toast.show('success', '"Password has been updated successfully!"');
    }, (err: any) => {
      this.isUpdatingPassword = false;
      this.isOtpSent = false;
      this.passwordForm.patchValue({
        newPassword: '',
        confirmPassword: ''
      });
      this.toast.show('error', "Failed to Update the Passwor");
    });
  }
  verifyOwnerPhone() {
    this.sendOtp$ = this.authService.sendMobileOtp(this.profileForm.get('phone')?.value, this.profileForm.value.countryCode, true);
    this.sendOtp$.subscribe(
      (data: any) => {
        if (data.resultCode == 0) {
          this.verifyphoneotp = false;
          this.toast.show('error', data.message);
        }

        if (data.resultCode != 0) {
          this.verifyphoneotp = true;
          this.startResendOtpTimer();
          this.toast.show('success', data.message);
        }
      },
      (err) => {
        this.toast.show('error', "Failed to Send an OTP");
      }
    );
  }
  showLogoutPopup = false;
  closeLogout() {
    this.showLogoutPopup = false;
  }
  confirmLogout() {
    const id = localStorage.getItem('org_Id')
    this.authService.cancelSubs(Number(id)).subscribe(
      (data: any) => {
        this.loading = false;
        if (this.authService.resultCodeError(data)) {
          return;
        }
        // this.getAdminList();
        this.getProfile();
        setTimeout(() => {
        }, 200);
        this.toast.show('success', data.resultDescription);
        this.closeLogout();
        // this.closeModal();

      },
      (error) => {
        this.authService.dataError(error);
      }
    );
    //  this.router.navigateByUrl('');
  }

}
